import * as i0 from '@angular/core';
import { InjectionToken, Inject, Injectable } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';

const UNIPIN_GQL_CONFIG = new InjectionToken('UniPinGqlConfig');
function provideUniPinGql(config) {
    return {
        provide: UNIPIN_GQL_CONFIG,
        useValue: config
    };
}

class GqlParser {
    constructor(config, http) {
        this.config = config;
        this.http = http;
        this.memoryCache = new Map();
    }
    query({ query, headers, variables, operationName, cacheStrategy = 'cache-first' }) {
        const cacheKey = this.generateCacheKey(query, operationName, variables);
        const cached = this.getFromCache(cacheKey);
        if (cacheStrategy === 'cache-only') {
            if (!cached) {
                throw new Error('Cache-only requested but no cache found');
            }
            return of({ data: cached });
        }
        if (cached) {
            if (cacheStrategy === 'cache-first') {
                return of({ data: cached });
            }
            if (cacheStrategy === 'cache-and-network') {
                return of({ data: cached }).pipe(switchMap(() => this.fetchQuery({
                    query,
                    headers,
                    variables,
                    operationName,
                }, cacheKey, true)));
            }
        }
        return this.fetchQuery({
            query,
            headers,
            variables,
            operationName
        }, cacheKey, cacheStrategy !== 'no-cache');
    }
    mutate({ headers, mutation, variables, operationName, }) {
        return this.http.post(this.getUrl(operationName), JSON.stringify({
            variables,
            operationName,
            query: mutation,
        }), {
            headers: new HttpHeaders({
                ...headers,
                'Content-Type': 'application/json'
            })
        })
            .pipe(map((response) => {
            if (response.errors) {
                throw new Error(this.getErrorMessage(response));
            }
            return {
                data: response.data
            };
        }), catchError((error) => {
            throw new Error(this.getErrorMessage(error));
        }));
    }
    fetchQuery({ query, headers, variables, operationName, }, cacheKey, useCache) {
        return this.http.post(this.getUrl(operationName), JSON.stringify({
            query,
            variables,
            operationName,
        }), {
            headers: new HttpHeaders({
                ...headers,
                'Content-Type': 'application/json'
            })
        }).pipe(map((response) => {
            if (response.errors) {
                throw new Error(this.getErrorMessage(response));
            }
            if (useCache && cacheKey) {
                this.setToCache(cacheKey, response.data);
            }
            return {
                data: response.data
            };
        }), catchError((error) => {
            throw new Error(this.getErrorMessage(error));
        }));
    }
    getFromCache(key) {
        return this.memoryCache.get(key) || null;
    }
    getUrl(operationName) {
        if (!this.config.useOperationPath)
            return this.config.uri;
        return `${this.config.uri}${operationName ? `/${operationName}` : ''}`;
    }
    generateCacheKey(query, operationName, variable) {
        return `${operationName}-${this.cacheHash(JSON.stringify(variable))}-${this.cacheHash(query)}`;
    }
    cacheHash(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = (hash << 5) - hash + str.charCodeAt(i);
            hash &= hash;
        }
        return hash.toString(36);
    }
    setToCache(key, data) {
        const oldestKey = this.memoryCache.keys().next().value;
        if (this.memoryCache.has(key)) {
            this.memoryCache.delete(key);
        }
        if (oldestKey &&
            this.memoryCache.size >= (this.config.cacheSize || 100)) {
            this.memoryCache.delete(oldestKey);
        }
        this.memoryCache.set(key, data);
    }
    getErrorMessage(e) {
        if (e?.error?.errors?.length > 0) {
            return e?.error?.errors[0].message || 'Unknown error';
        }
        if (e instanceof HttpErrorResponse) {
            switch (e.status) {
                case 401: return 'Unauthorized: Please check your credentials';
                case 404: return 'Not Found: Resource could not be located';
                case 500: return 'Server Error: Something went wrong on the server';
                default: return e.statusText || 'Unknown error occurred';
            }
        }
        return e?.message || 'Unknown error occurred';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: GqlParser, deps: [{ token: UNIPIN_GQL_CONFIG }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: GqlParser, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.7", ngImport: i0, type: GqlParser, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [UNIPIN_GQL_CONFIG]
                }] }, { type: i1.HttpClient }] });

/*
 * Public API Surface of unipin-angular-gql
 */

/**
 * Generated bundle index. Do not edit.
 */

export { GqlParser, UNIPIN_GQL_CONFIG, provideUniPinGql };
